import React, {useState, useEffect} from 'react';
import {Typography, Card, Row, Col, Button, Progress, Result} from 'antd';
import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import {ReloadOutlined, ArrowRightOutlined} from '@ant-design/icons';
import ChatPromoBanner from "../Fragments/ChatPromoBanner";
import psychoData from '../../psycho_tests.json';

const {Title, Paragraph, Text} = Typography;

const PsychoTests = ({analytics}) => {
    const {t, i18n} = useTranslation();
    const isRu = i18n.language === 'ru';
    const [step, setStep] = useState(0);
    const [selected, setSelected] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [passedAll, setPassedAll] = useState(false);

    useEffect(() => {
        if (analytics) analytics();
    }, [analytics]);

    const currentQuestion = psychoData[step];

    const handleSelect = (index) => {
        if (selected !== null) return;
        
        setSelected(index);
        const correct = index === currentQuestion.answer;
        setIsCorrect(correct);

        if (correct) {
            if (step === psychoData.length - 1) {
                setTimeout(() => setPassedAll(true), 1000);
            }
        }
    };

    const handleNext = () => {
        setSelected(null);
        setIsCorrect(null);
        setStep(step + 1);
    };

    const resetTest = () => {
        setStep(0);
        setSelected(null);
        setIsCorrect(null);
        setPassedAll(false);
    };

    if (passedAll) {
        return (
            <div style={{maxWidth: '800px', margin: '40px auto', padding: '20px'}}>
                <Result
                    status="success"
                    title={isRu ? "Поздравляем, вы прошли психологический тест" : "Շնորհավորում ենք, դուք անցաք հոգեբանական թեստը"}
                    subTitle={isRu ? "Теперь вы можете с уверенностью переходить к основным экзаменационным тестам." : "Այժմ կարող եք վստահորեն անցնել հիմնական քննական թեստերին:"}
                    extra={[
                        <Button type="primary" key="tests" onClick={() => window.location.href = '/quizes'}>
                            {isRu ? "Перейти к тестам" : "Անցնել Թեստերին"}
                        </Button>,
                        <Button key="reset" onClick={resetTest}>
                            {isRu ? "Попробовать еще раз" : "Փորձել նորից"}
                        </Button>,
                    ]}
                />
            </div>
        );
    }

    return (
        <div style={{maxWidth: '800px', margin: '0 auto', padding: '20px'}}>
            <Helmet>
                <title>{t('header.psycho')} - AvtoDas</title>
                <meta name="description" content={isRu ? "Психологические тесты для теоретического экзамена на водительские права." : "Վարորդական իրավունքի տեսական քննության հոգեբանական թեստեր:"} />
            </Helmet>



            <Card style={{marginBottom: 20, borderRadius: 12, textAlign: 'center'}}>
                <Title level={2}>{t('header.psycho')}</Title>
                <Paragraph type="secondary">
                    {isRu ? "Перед переходом к вопросам теоретического экзамена, кандидат в водители должен ответить на три подобных вопроса." : "Մինչև տեսական քննության հարցերին անցնելը, վարորդի թեկնածուն պետք է պատասխանի նմանատիպ երեք հարցի:"}
                </Paragraph>
                <Progress 
                    percent={Math.round((step / psychoData.length) * 100)} 
                    strokeColor={{'0%': '#108ee9', '100%': '#87d068'}}
                />
            </Card>

            <div className="question-item-fancy">
                <Card 
                    bordered={false} 
                    style={{ background: '#fcfcfc', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                    <Title level={4} style={{ marginBottom: 20 }}>
                        {isRu && currentQuestion.question_ru ? currentQuestion.question_ru : currentQuestion.question}
                    </Title>
                    
                    <div style={{ textAlign: 'center', marginBottom: 25 }}>
                        <img 
                            src={require(`../../assets/psycho/${currentQuestion.image}`)} 
                            alt="Psycho Test"
                            style={{ borderRadius: 12, border: '1px solid #f0f0f0', maxWidth: '100%', maxHeight: '300px' }}
                        />
                    </div>

                    <div className="variants-container">
                        <Row gutter={[12, 12]}>
                            {currentQuestion.variants.map((variant, index) => {
                                let statusClass = "";
                                if (selected !== null) {
                                    if (index === currentQuestion.answer) statusClass = "correct";
                                    else if (index === selected) statusClass = "incorrect";
                                } else if (selected === index) {
                                    statusClass = "selected";
                                }

                                return (
                                    <Col xs={24} key={index}>
                                        <div 
                                            className={`variant-btn ${statusClass}`}
                                            onClick={() => handleSelect(index)}
                                        >
                                            <span className="variant-index">{index + 1}</span>
                                            <span className="variant-text">
                                                {isRu && variant.value_ru ? variant.value_ru : variant.value}
                                            </span>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>

                    {isCorrect === false && (
                        <div style={{ marginTop: 20 }}>
                            <Paragraph type="danger" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                {isRu ? "Неправильный ответ: Чтобы пройти психологический тест, необходимо ответить на все вопросы без ошибок." : "Սխալ պատասխան: Հոգեբանական թեստը անցնելու համար անհրաժեշտ է պատասխել բոլոր հարցերին առանց սխալների:"}
                            </Paragraph>
                            <Button block type="primary" danger onClick={resetTest} icon={<ReloadOutlined />}>
                                {isRu ? "Начать заново" : "Սկսել նորից"}
                            </Button>
                        </div>
                    )}

                    {isCorrect === true && step < psychoData.length - 1 && (
                        <div style={{ marginTop: 30, textAlign: 'center' }}>
                            <Button 
                                type="primary" 
                                size="large" 
                                icon={<ArrowRightOutlined />} 
                                onClick={handleNext}
                                style={{ minWidth: 200, borderRadius: 8 }}
                            >
                                {isRu ? "Следующий" : "Հաջորդը"}
                            </Button>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default PsychoTests;
